import { Link, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import {
  accessibleTextRenderer,
  notSetCell,
} from "../../../Shared/TableRenderers";
import {
  ApprovalLinkData,
  Ev2ExperimentStage,
  PipelineRun,
  PipelineRunUserData,
  PipelineStatus,
} from "../../../Types";
import { styles } from "./rolloutDetailsTable.styles";
import {
  formatDate,
  getPipelineRunUrl,
  parseRolloutType,
} from "../rolloutDetails.utils";
import {
  getExpTreatment,
  getStatusColor,
  getStatusIcon,
  normalizeEv2Status,
  normalizeStatus,
} from "../../../Shared/shared.utils";
import * as moment from "moment";
import { baseTrackNavigationEvent, NavType } from "../../../Shared/AppInsights";

/**
 * Renders the Run ID column with a link to the pipeline run and displays a failed icon if the pipeline failed.
 * @param run - The pipeline run data
 * @param ev2StatusMap - Map of run IDs to their EV2 experiment statuses
 * @param ev2StatusLoading - Map of run IDs to their EV2 loading states
 * @param loadingApprovals - Map of run IDs to their approval loading states
 */
export function runIdRenderer(
  run: PipelineRun,
  ev2StatusMap: Record<number, Ev2ExperimentStage[]>,
  ev2StatusLoading: Record<number, boolean>,
  loadingApprovals: Record<number, boolean>,
) {
  if (!run.id) {
    return notSetCell("Run ID not set");
  }

  // EV2 Status
  const ev2Statuses = ev2StatusMap[run.id] ?? [];
  const latestEv2Status = ev2Statuses[ev2Statuses.length - 1];
  const ev2Status = normalizeStatus(
    latestEv2Status?.status || PipelineStatus.PENDING,
  );
  // ADO Pipeline Status
  const isLoading = ev2StatusLoading[run.id] || loadingApprovals[run.id];
  const runStatus = normalizeStatus(run.result);
  const showFailed =
    !isLoading &&
    ev2Status === PipelineStatus.PENDING &&
    runStatus === PipelineStatus.FAILED;
  const pipelineRunUrl = getPipelineRunUrl(run);

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
      <Link
        href={pipelineRunUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.rolloutDetailsColumnRunIdLink}
        onMouseOver={(e) => {
          e.currentTarget.style.textDecoration = "underline";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.textDecoration = "none";
        }}
        onClick={() => {
          baseTrackNavigationEvent({
            component: "RolloutDetailsTable",
            destination: pipelineRunUrl,
            navType: NavType.External,
          });
        }}
      >
        {accessibleTextRenderer(run.id.toString())}
      </Link>
      {showFailed && (
        <span
          style={{ color: getStatusColor(runStatus), display: "flex" }}
          title={"ADO Pipeline failed"}
        >
          {getStatusIcon(runStatus)}
        </span>
      )}
    </Stack>
  );
}

/**
 * Renders the Environment column by parsing the environment from the RolloutAndProgressionType.
 * @param run - The pipeline run data
 */
export function environmentRenderer(run: PipelineRun) {
  if (!run.templateParameters?.RolloutAndProgressionType) {
    return notSetCell("Environment not set");
  }
  const { environment } = parseRolloutType(
    run.templateParameters.RolloutAndProgressionType,
  );
  return (
    <span className={styles.rolloutDetailsColumnEnvironment}>
      {accessibleTextRenderer(environment)}
    </span>
  );
}

/**
 * Renders the Mode column by parsing the mode from the RolloutAndProgressionType.
 * @param run - The pipeline run data
 */
export function modeRenderer(run: PipelineRun) {
  if (!run.templateParameters?.RolloutAndProgressionType) {
    return notSetCell("Mode not set");
  }
  const { mode } = parseRolloutType(
    run.templateParameters.RolloutAndProgressionType,
  );
  return (
    <span className={styles.rolloutDetailsColumnEnvironment}>
      {accessibleTextRenderer(mode)}
    </span>
  );
}

/**
 * Renders the Stages column showing approval, EV2, and Experiment Studio stages with their statuses.
 * @param run - The pipeline run data
 * @param approvalData - Map of run IDs to their approval data (link and status)
 * @param loadingApprovals - Map of run IDs to their approval loading states
 * @param ev2StatusMap - Map of run IDs to their EV2 experiment statuses
 * @param ev2StatusLoading - Map of run IDs to their EV2 loading states
 * @param onEv2StageClicked - Callback function when EV2 stage is clicked
 */
export function stagesRenderer(
  run: PipelineRun,
  approvalData: Record<number, ApprovalLinkData | null>,
  loadingApprovals: Record<number, boolean>,
  ev2StatusMap: Record<number, Ev2ExperimentStage[]>,
  ev2StatusLoading: Record<number, boolean>,
  onEv2StageClicked: (runId: number) => void,
  abnVariantCount?: number,
) {
  // EV2 Status
  const ev2Statuses = (ev2StatusMap[run.id] ?? []).sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );

  // Approval Status
  const approvalLoading = loadingApprovals[run.id] || ev2StatusLoading[run.id];
  const approvalLink = approvalData[run.id]?.approvalLink;
  const approvalStatus = normalizeStatus(
    approvalData[run.id]?.status || PipelineStatus.RUNNING,
  );
  const approvalStatusTooltip =
    approvalStatus === PipelineStatus.RUNNING && !approvalLink
      ? "Awaiting approval link..."
      : approvalStatus === PipelineStatus.RUNNING && approvalLink
        ? "Awaiting approval..."
        : undefined;

  return (
    <Stack
      horizontal
      tokens={{
        childrenGap: "8px",
      }}
    >
      {statusRenderer(
        "Approval",
        approvalStatus,
        approvalLoading,
        approvalLink,
        undefined,
        approvalStatusTooltip,
      )}
      {ev2StageRenderer(ev2Statuses, ev2StatusLoading[run.id], () =>
        onEv2StageClicked(run.id),
      )}
      {expStudioStageRenderer(
        ev2Statuses,
        ev2StatusLoading[run.id],
        abnVariantCount,
      )}
    </Stack>
  );
}

/**
 * Renders the EV2 stage status. Will display health checks as warnings instead of errors.
 * @param ev2Stages - Array of EV2 experiment stages
 * @param loading - Loading state for EV2 stages
 * @param onEv2StageClicked - Callback function when EV2 stage is clicked
 * @returns JSX element representing the EV2 stage status
 */
function ev2StageRenderer(
  ev2Stages: Ev2ExperimentStage[],
  loading: boolean,
  onEv2StageClicked: () => void,
) {
  const latestStage = ev2Stages[0];

  if (!latestStage) {
    return (
      <Stack horizontal tokens={{ childrenGap: "8px" }}>
        {statusRenderer(
          "EV2",
          PipelineStatus.PENDING,
          loading,
          undefined,
          undefined,
        )}
      </Stack>
    );
  }

  let status = normalizeEv2Status(latestStage);
  let statusTooltip =
    status === PipelineStatus.WARNING
      ? "Health check failed"
      : latestStage.error?.reason;

  return (
    <Stack horizontal tokens={{ childrenGap: "8px" }}>
      {statusRenderer(
        "EV2",
        status,
        false,
        undefined,
        onEv2StageClicked,
        statusTooltip,
      )}
    </Stack>
  );
}

/**
 * Renders the Experiment Studio stage status.
 * @param ev2Statuses - Array of EV2 experiment stages
 * @param loading - Loading state for EV2 stages
 * @returns JSX element representing the Experiment Studio stage status
 */
function expStudioStageRenderer(
  ev2Statuses: Ev2ExperimentStage[],
  loading: boolean,
  abnVariantCount?: number,
) {
  // EXP Studio Status
  const experimentRolloutActions = ev2Statuses.filter(
    (status) => status.rolloutAction === "extension/RolloutExperiment",
  );
  const experimentRolloutAction = experimentRolloutActions[0];
  const expStudioLink = experimentRolloutAction?.links?.experiment;
  if (!experimentRolloutAction || !expStudioLink) {
    return statusRenderer("EXP Running", PipelineStatus.PENDING, false, null);
  }

  const expStatus =
    experimentRolloutAction.status === "Succeeded"
      ? PipelineStatus.COMPLETED
      : PipelineStatus.PENDING;

  const treatment = getExpTreatment(
    experimentRolloutAction.stageName || "",
    true,
    abnVariantCount,
  );
  return statusRenderer(
    `EXP Running (${treatment})`,
    expStatus,
    loading,
    expStudioLink,
  );
}

/**
 * Generates the EV2 portal URL for a specific experiment stage.
 * @param ev2Stage - The EV2 experiment stage data
 * @param ev2BaseLink - The base URL for the EV2 portal
 */
export function getEv2StageUrl(
  ev2Stage?: Ev2ExperimentStage,
  ev2BaseLink?: string | null,
) {
  if (!ev2Stage || !ev2BaseLink) {
    return null;
  }
  const { stageName, rolloutAction, stamp } = ev2Stage;
  const encodedStageName = encodeURIComponent(stageName);
  const region = stageName.split(".")[1];
  // Generate the correct action stamp based on the rollout action type
  let actionPrefix = "rolloutExperiment";
  if (rolloutAction === "mdmHealthCheck/MonitorExperiment") {
    actionPrefix = "monitorExperimentHealth";
  }
  const actionStamp = encodeURIComponent(`${actionPrefix}-${region}-${stamp}`);
  const encodedAction = encodeURIComponent(rolloutAction);

  return `${ev2BaseLink}/list/${encodedStageName}/${actionStamp}/${encodedAction}/0/_overview`;
}

/**
 * Renders a status cell with an icon, optional link, and optional click handler.
 * @param title - The text to display in the status cell
 * @param status - The pipeline status determining the icon and color
 * @param isLoading - Whether to show a loading spinner instead of the status icon
 * @param link - Optional URL to make the title a hyperlink
 * @param onClick - Optional click handler to make the title clickable
 * @param statusTooltip - Optional tooltip text to display on hover
 */
export function statusRenderer(
  title: string | JSX.Element,
  status: PipelineStatus,
  isLoading: boolean,
  link?: string | null,
  onClick?: () => void,
  statusTooltip?: string,
  componentSource?: string,
) {
  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: "2px" }}>
      {isLoading ? (
        <Spinner size={SpinnerSize.small} />
      ) : (
        <span
          style={{ color: getStatusColor(status), display: "flex" }}
          title={statusTooltip || status}
        >
          {getStatusIcon(status)}
        </span>
      )}
      <span></span>
      {link ? (
        <Link
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            baseTrackNavigationEvent({
              component: componentSource ?? "RolloutDetailsTable",
              destination: link,
              navType: NavType.External,
              properties: {
                title: typeof title === "string" ? title : undefined,
                status,
              },
            });
          }}
        >
          {typeof title === "string" ? accessibleTextRenderer(title) : title}
        </Link>
      ) : onClick ? (
        <Link onClick={onClick}>
          {typeof title === "string" ? accessibleTextRenderer(title) : title}
        </Link>
      ) : (
        <span className={styles.rolloutDetailsColumnEnvironment}>
          {typeof title === "string" ? accessibleTextRenderer(title) : title}
        </span>
      )}
    </Stack>
  );
}

/**
 * Renders the Started By column showing the user who initiated the pipeline run.
 * @param run - The pipeline run data containing user information
 */
export function startedByRenderer(run: PipelineRunUserData) {
  const startedBy = run.requestedBy?.uniqueName || "Unknown";
  return accessibleTextRenderer(startedBy);
}

/**
 * Renders the Creation Date column with a formatted date string.
 * @param run - The pipeline run data
 */
export function creationDateRenderer(run: PipelineRun) {
  if (!run.createdDate) {
    return notSetCell("Creation date not set");
  }

  return (
    <span className={styles.rolloutDetailsColumnCreationDate}>
      {accessibleTextRenderer(formatDate(run.createdDate))}
    </span>
  );
}

/**
 * Renders the Duration column showing how long the pipeline run took to complete.
 * @param run - The pipeline run data
 */
export function durationRenderer(run: PipelineRun) {
  return (
    <span className={styles.rolloutDetailsColumnCreationDate}>
      {run.createdDate && run.finishedDate
        ? accessibleTextRenderer(
            moment
              .duration(
                new Date(run.finishedDate).getTime() -
                  new Date(run.createdDate).getTime(),
              )
              .humanize(),
          )
        : run.result === "inProgress"
          ? accessibleTextRenderer("Running...")
          : notSetCell("N/A")}
    </span>
  );
}
